import type {
  AccommodationCatalogConsoleListingRedirectMechanismsFragment,
  AccommodationConsoleListingPropertyFragment,
  AccommodationPlacesPricingAndBookingDetailsQuery,
} from '../AccommodationsConsole.graphql.generated';
import { useAccommodationPlacesByCoupleQuery, useAccommodationsConsolePropertiesQuery } from '../AccommodationsConsole.graphql.generated';
import { AccommodationPlaceType } from '@graphql/generated-base';
import { PriceSource } from '@shared/constants/accommodations';
import { addAction, addError } from '@shared/utils/logger';
import { useMemo } from 'react';
import type {
  AccommodationListingDTO,
  AccommodationListingDTOBase,
  AccommodationPlace,
  AccommodationsConsoleState,
  RoomBlockAccommodationListingDTO,
  RoomBlockWithPricePerNight,
} from '@shared/types/Accommodations.types';
import { validateTravelOfferDiag } from '@shared/types/TravelOfferDiag.validation';
import type { UseHotelRoomBlockPriceMappingReturn } from './useListingPriceMapping';
import { useHotelRoomBlockPriceMapping, useStandardHotelRoomPriceMapping } from './useListingPriceMapping';
import type { ApolloError } from '@apollo/client';
import { isValidLatLngLocation } from '@shared/utils/locationValidation';
import { useApiMetadata } from '@shared/hooks/useApiMetadata';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';
import { hasAnyCancellationPolicyFree } from '@shared/utils/accommodations/AccommodationTiles';
import { useAccommodationPlacesPricing } from './useAccommodationPlacesPricing';

// Uses the query-level type (not the fragment type) because the merged GraphQL
// selection includes cancellationPolicies which the fragment type doesn't have
// until codegen is re-run against the updated schema.
type PricingAndBookingPlace = AccommodationPlacesPricingAndBookingDetailsQuery['travelPlacesPricingAndBookingDetails']['accommodationPlaces'][number];

type PricingAndBookingByPlaceId = Readonly<{
  pricing: PricingAndBookingPlace['pricingInfo'];
  redirectMechanisms: Maybe<AccommodationCatalogConsoleListingRedirectMechanismsFragment[]>;
}>;

type SupportedJoyHotelAccommodation = Merge<
  AccommodationPlace,
  {
    name: string;
    lat: number;
    long: number;
    type: AccommodationPlaceType.hotel;
    isFromAccommodationCatalog: true;
    accommodationRoomBlock: RoomBlockWithPricePerNight | null;
  }
>;
type SupportedHotelRoomBlockAccommodation = Merge<
  AccommodationPlace,
  {
    name: string;
    lat: number;
    long: number;
    type: AccommodationPlaceType.custom;
    accommodationRoomBlock: RoomBlockWithPricePerNight;
  }
>;

type SupportEventAccommodationPlace = SupportedJoyHotelAccommodation | SupportedHotelRoomBlockAccommodation;

const isSupportedAccommodation = (place: AccommodationPlace, shouldFilterOutNonJoyRoomBlocks: boolean): place is SupportEventAccommodationPlace => {
  return (
    // Is named
    !!(place.name || place.customName) &&
    // Is one of the supported types:
    //  - A Joy hotel -- hotel or partner room block
    //  - A custom room block
    ((place.type === 'hotel' && place.isFromAccommodationCatalog) || (!shouldFilterOutNonJoyRoomBlocks && place.type === 'custom' && !!place.accommodationRoomBlock)) &&
    // If is room block, does it have a nightly rate
    (place.accommodationRoomBlock ? typeof place.accommodationRoomBlock.pricePerNight === 'number' : true) &&
    // Has coordinates (needs to be pinnable to the map)
    isValidLatLngLocation({ latitude: place.lat, longitude: place.long })
  );
};

type UseAccommodationsConsoleListingsArgs = Readonly<{
  state: AccommodationsConsoleState;
  skip?: boolean;
  shouldFilterOutNonJoyRoomBlocks: boolean;
  eventHandle: Maybe<string>;
}>;

const deriveRoomBlockListingInfo = (
  baseInfo: AccommodationListingDTOBase,
  place: Omit<SupportedHotelRoomBlockAccommodation, 'type'>,
  mapHotelRoomBlockPrice: UseHotelRoomBlockPriceMappingReturn,
): RoomBlockAccommodationListingDTO => {
  const maybePhotoUrl = (place.photo || place.customPhoto)?.url;
  const roomBlock = place.accommodationRoomBlock;
  // Need to handle the scenario of roomblock cutoff date
  return {
    ...baseInfo,
    type: 'roomBlock',
    bookingUrl: roomBlock.bookingUrl,
    cutoffDate: roomBlock.cutoffDate,
    interestedGuestCount: roomBlock.interestedGuestCount,
    numberOfRoomsReserved: roomBlock.numberOfRoomsReserved,
    mediaAssets: {
      isLoading: false,
      photos:
        // If there are photos in the base info, those photos are provided by the joy accommodation.
        // We prefer those.
        baseInfo.mediaAssets.photos.length
          ? baseInfo.mediaAssets.photos
          : maybePhotoUrl
            ? [
                {
                  url: maybePhotoUrl,
                },
              ]
            : [],
    },
    pricingInfo: {
      isLoading: false,
      values: mapHotelRoomBlockPrice(roomBlock),
    },
  };
};

type DeriveBaseAccommodationListingInfoFromEventAccommodationArgs = {
  place: SupportEventAccommodationPlace;
  extendedAccommodationData: AccommodationConsoleListingPropertyFragment | null;
  isLoadingExtendedAccommodationData: boolean;
  errorLoadingExtendedAccommodationsData: Maybe<ApolloError>;
};
const deriveBaseAccommodationListingInfoFromEventAccommodation = ({
  place,
  extendedAccommodationData,
  isLoadingExtendedAccommodationData,
  errorLoadingExtendedAccommodationsData,
}: DeriveBaseAccommodationListingInfoFromEventAccommodationArgs): AccommodationListingDTOBase => {
  return {
    isPickedByCouple: true,

    // The following fields are directly available on the event accommodation
    id: place.id,
    joyPlaceId: place.joyPlaceId,
    name: place.customName || place.name,
    isFavorite: place.isFavorite,
    isHostStayingHere: place.isHostStayingHere,
    userNote: place.userNote,
    location: {
      lat: place.lat,
      lng: place.long,
    },
    address: {
      street: place.address,
      city: place.city,
      country: place.country,
    },
    starRating: place.starRating,

    // Need to use the `searchAccommodationProperties` API data for the following fields:
    mediaAssets: extendedAccommodationData
      ? {
          isLoading: extendedAccommodationData.photos ? false : isLoadingExtendedAccommodationData,
          photos: extendedAccommodationData.photos,
        }
      : {
          isLoading: !errorLoadingExtendedAccommodationsData,
          photos: (place.photo?.url && [{ url: place.photo.url }]) || [],
        },
    userRating: extendedAccommodationData?.userRating
      ? {
          ratingValue: extendedAccommodationData.userRating.ratingValue,
          ratingCount: extendedAccommodationData.userRating.ratingCount,
        }
      : null,
    facilities: extendedAccommodationData?.facilities || [],
    accommodationCategory: extendedAccommodationData?.accommodationCategory,
    chain: extendedAccommodationData?.chain ?? null,
    // Couple-picks and room blocks lack catalog pricing data, so cancellation info is unavailable.
    // Joy hotels override this in the standardHotel branch via hasAnyCancellationPolicyFree().
    hasFreeCancellation: false,
  };
};

export const useAccommodationPlacesByCouple = (args: UseAccommodationsConsoleListingsArgs) => {
  const { state, skip = false, shouldFilterOutNonJoyRoomBlocks, eventHandle } = args;
  const shouldSkip = skip || !eventHandle;

  const { deviceType, anonymousId, eventId, userCountryCode, isMetadataLoading } = useApiMetadata();

  const { data, loading } = useAccommodationPlacesByCoupleQuery({
    variables: {
      name: eventHandle || '',
    },
    skip: shouldSkip,
    ssr: false,
    batchMode: 'fast',
    onCompleted(data) {
      if (!data?.eventByName?.accommodationPlacesV2?.length) {
        addAction('Accommodation places by couple is empty (AccommodationsConsole)', {
          context: 'accommodationPlaces',
          eventHandle,
        });
      }
    },
    onError: error => {
      addError(error, {
        source: 'Accommodation places by couple api failed (AccommodationsConsole)',
        context: 'accommodationPlaces',
        errorMessage: error.message,
        eventHandle,
      });
    },
  });

  const accommodationPlaceIds = data?.eventByName?.accommodationPlacesV2?.map(x => x.id) || [];
  const accommodationPlaceIdsToFetchPrices = data?.eventByName?.accommodationPlacesV2?.filter(accom => accom.type === 'hotel').map(x => x.id) || [];

  // Load pricing data
  const {
    data: pricingData,
    loading: isFetchingListingPrices,
    error: errorFetchingAccommodationPrices,
  } = useAccommodationPlacesPricing(accommodationPlaceIdsToFetchPrices, {
    dates: {
      // Fetch event accommodation prices based on **state** search dates
      checkIn: state.searchDates.checkin,
      checkOut: state.searchDates.checkout,
    },
    occupancies: state.occupancies,
  });

  // Get extended joy hotel data
  // Progressively layer in this data as it loads
  const {
    data: extendedAccommodationData,
    loading: isLoadingExtendedAccommodationData,
    error: errorLoadingExtendedAccommodationsData,
  } = useAccommodationsConsolePropertiesQuery({
    variables: {
      payload: {
        propertyIdentifiers: accommodationPlaceIds.map(x => ({ accommodationPlaceId: x })),
        metadata: {
          eventId,
          deviceType,
          anonymousId,
          userCountryCode,
        },
      },
    },
    skip: !accommodationPlaceIds.length || isMetadataLoading,
    onError: error =>
      addError(error, {
        source: 'Search accommodations properties with propertyIdentifiers api failed (AccommodationsConsole)',
        context: 'searchAccommodationProperties',
        errorMessage: error.message,
        propertyIdentifiers: accommodationPlaceIds,
      }),
    onCompleted(data) {
      if (!data?.searchAccommodationProperties?.properties?.length) {
        addAction('Accommodation properties is empty (AccommodationsConsole)', {
          context: 'searchAccommodationProperties',
          propertyIdentifiers: accommodationPlaceIds,
        });
      }
    },
    batchMode: 'off',
  });

  const eventCurrencyCode = data?.eventByName?.travelSettings?.currencyCode;
  const mapStandardHotelRoomPrice = useStandardHotelRoomPriceMapping();
  const mapHotelRoomBlockPrice = useHotelRoomBlockPriceMapping(eventCurrencyCode);

  const filteredAccommodationPlaces = useMemo(() => {
    const supportedAccommodations =
      data?.eventByName?.accommodationPlacesV2?.filter((place): place is SupportEventAccommodationPlace => isSupportedAccommodation(place, shouldFilterOutNonJoyRoomBlocks)) || [];

    const pricingAndBookingByPlaceId =
      pricingData?.travelPlacesPricingAndBookingDetails?.accommodationPlaces?.reduce(
        (acc, place) => {
          acc[place.accommodationPlace.id] = {
            pricing: place.pricingInfo,
            redirectMechanisms: place.redirectMechanisms,
          };
          return acc;
        },
        {} as { [accommodationId: string]: PricingAndBookingByPlaceId },
      ) || {};

    const extendedAccommodationDataByJoyPlaceId =
      extendedAccommodationData?.searchAccommodationProperties?.properties.reduce(
        (acc, property) => {
          acc[property.joyPlaceId] = property;
          return acc;
        },
        {} as { [joyPlaceId: string]: AccommodationConsoleListingPropertyFragment },
      ) || {};

    return supportedAccommodations.map<AccommodationListingDTO>(place => {
      const extendedAccommodationData = place.joyPlaceId ? extendedAccommodationDataByJoyPlaceId[place.joyPlaceId] : null;

      const baseInfo = deriveBaseAccommodationListingInfoFromEventAccommodation({
        place,
        extendedAccommodationData,
        isLoadingExtendedAccommodationData,
        errorLoadingExtendedAccommodationsData,
      });

      // Custom room block
      if (place.type === AccommodationPlaceType.custom) {
        return deriveRoomBlockListingInfo(baseInfo, place, mapHotelRoomBlockPrice);
      }
      // Partner hotel room block
      else if (place.accommodationRoomBlock) {
        return deriveRoomBlockListingInfo(
          baseInfo,
          // Need to spread the object so that TypeScript recognizes the type correctly
          { ...place, accommodationRoomBlock: place.accommodationRoomBlock },
          mapHotelRoomBlockPrice,
        );
      } else {
        // Handle the joy hotel
        const placeData = pricingAndBookingByPlaceId[place.id];
        const pricingInfo = placeData?.pricing;
        // Null when the payload carries no per-night amount, even though pricingInfo is present.
        const pricingInfoValue = pricingInfo ? mapStandardHotelRoomPrice(pricingInfo) : null;
        return {
          ...baseInfo,
          type: 'standardHotel',
          diag: placeData?.pricing?._diag ? validateTravelOfferDiag(placeData.pricing._diag) : null,
          hasFreeCancellation: hasAnyCancellationPolicyFree(placeData?.pricing?.cancellationPolicies),
          isSoldOutForSelectedDates: !!extractHotelRedirects(placeData?.redirectMechanisms)?.hotelSearchPageUrl,
          redirectMechanisms: placeData?.redirectMechanisms || [],
          // Guard on pricingInfo, not the mapped value: a settled no-price place has finished loading.
          pricingInfo: pricingInfo
            ? {
                isLoading: false,
                values: pricingInfoValue,
                // useAccommodationPlacesPricing uses a no-cache fetch policy, so this is always live.
                source: pricingInfoValue ? PriceSource.LIVE : undefined,
              }
            : {
                isLoading: isFetchingListingPrices,
                values: null,
              },
        };
      }
    });
  }, [
    extendedAccommodationData,
    isLoadingExtendedAccommodationData,
    errorLoadingExtendedAccommodationsData,
    data?.eventByName?.accommodationPlacesV2,
    pricingData,
    isFetchingListingPrices,
    mapStandardHotelRoomPrice,
    mapHotelRoomBlockPrice,
    shouldFilterOutNonJoyRoomBlocks,
  ]);

  return {
    eventAccommodations: filteredAccommodationPlaces,
    // Keep loading false when this query is skipped (OTA path), so parent loading
    // state stays aligned across SSR hydrate and CSR-only execution paths.
    isFetchingEventAccommodations: !shouldSkip && loading,
    errorFetchingAccommodationPrices,
  };
};
